import { useCallback, useEffect, useState } from "react";

export const VIEW_FULLSCREEN_ROOT_ID = "prometal-view-fullscreen-root";

const FALLBACK_ATTRIBUTE = "data-view-fullscreen-fallback";
const FULLSCREEN_CHANGE_EVENT = "prometal:view-fullscreen-change";

export type ViewFullscreenMode = "native" | "fallback" | null;

const getFullscreenRoot = (): HTMLElement | null => {
    if (typeof document === "undefined") return null;
    return document.getElementById(VIEW_FULLSCREEN_ROOT_ID);
};

const getFullscreenMode = (): ViewFullscreenMode => {
    if (typeof document === "undefined") return null;

    const root = getFullscreenRoot();
    if (!root) return null;

    if (document.fullscreenElement === root) return "native";
    if (root.getAttribute(FALLBACK_ATTRIBUTE) === "true") return "fallback";

    return null;
};

const emitFullscreenChange = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(FULLSCREEN_CHANGE_EVENT));
};

const setFallbackFullscreen = (root: HTMLElement, enabled: boolean) => {
    if (enabled) {
        root.setAttribute(FALLBACK_ATTRIBUTE, "true");
    } else {
        root.removeAttribute(FALLBACK_ATTRIBUTE);
    }

    emitFullscreenChange();
};

/**
 * Управляет fullscreen-режимом вкладки «Просмотр».
 *
 * 1. Сначала пытается использовать нативный Fullscreen API браузера.
 * 2. Если API недоступен или заблокирован (например, iframe без allowfullscreen),
 *    переключается на viewport-fallback через fixed-контейнер.
 * 3. Esc закрывает fallback-режим; нативный режим браузер закрывает самостоятельно.
 */
export const useViewFullscreen = () => {
    const [mode, setMode] = useState<ViewFullscreenMode>(() => getFullscreenMode());

    useEffect(() => {
        const syncMode = () => setMode(getFullscreenMode());

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape" || getFullscreenMode() !== "fallback") return;

            const root = getFullscreenRoot();
            if (root) setFallbackFullscreen(root, false);
        };

        document.addEventListener("fullscreenchange", syncMode);
        document.addEventListener("keydown", onKeyDown);
        window.addEventListener(FULLSCREEN_CHANGE_EVENT, syncMode);

        syncMode();

        return () => {
            document.removeEventListener("fullscreenchange", syncMode);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener(FULLSCREEN_CHANGE_EVENT, syncMode);
        };
    }, []);

    const enterFullscreen = useCallback(async (): Promise<ViewFullscreenMode> => {
        const root = getFullscreenRoot();
        if (!root) {
            throw new Error("Fullscreen-контейнер просмотра не найден");
        }

        const currentMode = getFullscreenMode();
        if (currentMode) return currentMode;

        const canUseNativeFullscreen =
            document.fullscreenEnabled &&
            typeof root.requestFullscreen === "function" &&
            document.fullscreenElement === null;

        if (canUseNativeFullscreen) {
            try {
                await root.requestFullscreen();
                return "native";
            } catch (error) {
                // В iframe Fullscreen API может быть запрещет политикой Permissions Policy.
                // В таком случае не ломаем UX и используем viewport-fallback.
                console.warn("Native fullscreen is unavailable; using viewport fallback", error);
            }
        }

        setFallbackFullscreen(root, true);
        return "fallback";
    }, []);

    const exitFullscreen = useCallback(async () => {
        const root = getFullscreenRoot();
        if (!root) return;

        if (document.fullscreenElement === root) {
            await document.exitFullscreen();
            return;
        }

        if (root.getAttribute(FALLBACK_ATTRIBUTE) === "true") {
            setFallbackFullscreen(root, false);
        }
    }, []);

    const toggleFullscreen = useCallback(async () => {
        if (getFullscreenMode()) {
            await exitFullscreen();
            return;
        }

        await enterFullscreen();
    }, [enterFullscreen, exitFullscreen]);

    return {
        mode,
        isFullscreen: mode !== null,
        enterFullscreen,
        exitFullscreen,
        toggleFullscreen,
    };
};
